
import { useTranslations } from "use-intl";
import { LayoutDashboard, FileText, BarChart3, Gem, User } from "lucide-react";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/utils/cn";

/**
 * Matches the reference mockups' bottom tab bar (Home / Practice /
 * Results / Ranking / Profile). Fixed to the bottom, visible only below
 * the `lg` breakpoint where StudentSidebar hides itself — this is the
 * actual fix for a real gap: without this, a signed-in user on a phone
 * (SiteHeader's own nav links are `hidden sm:block`) had no way to reach
 * /dashboard, /exams, /ranking, /pricing, or /profile except typing the
 * URL directly.
 *
 * Pricing was originally left off this bar on purpose (reachable from the
 * sidebar's Account section on larger screens, and from /profile on
 * mobile) — added back per explicit user request, since on mobile the
 * sidebar doesn't exist at all and /profile is an extra tap away.
 */
// Short one-word labels: the sidebar's full names ("Boshqaruv paneli", "Mock imtihonlar") wrapped
// onto two lines in a five-tab bar on a phone.
const ITEMS = [
  { href: "/dashboard", labelKey: "nav.mobile.home", Icon: LayoutDashboard },
  { href: "/exams", labelKey: "nav.mobile.mocks", Icon: FileText },
  { href: "/stats", labelKey: "nav.mobile.results", Icon: BarChart3 },
  { href: "/pricing", labelKey: "nav.mobile.pricing", Icon: Gem },
  { href: "/profile", labelKey: "nav.mobile.profile", Icon: User },
] as const;

export function StudentMobileNav() {
  const t = useTranslations("exams");
  const pathname = usePathname();

  return (
    <nav
      aria-label={t("nav.mockTests")}
      className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden"
    >
      {ITEMS.map(({ href, labelKey, Icon }) => {
        const isActive =
          pathname === href ||
          pathname.startsWith(`${href}/`) ||
          (href === "/stats" && ["/streak", "/achievements", "/ranking"].includes(pathname));
        return (
          <Link
            key={href}
            href={href}
            aria-current={isActive ? "page" : undefined}
            className={cn(
              "flex min-w-0 flex-col items-center gap-0.5 px-1 pb-2 pt-1.5 text-[11px] font-medium leading-tight transition-colors",
              isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span
              className={cn(
                "flex h-7 w-12 items-center justify-center rounded-full transition-colors",
                isActive && "bg-primary/15",
              )}
            >
              <Icon className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="w-full truncate text-center">{t(labelKey)}</span>
          </Link>
        );
      })}
    </nav>
  );
}
